import _ from 'lodash';

import {
  finleyAssert,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  CreditFacility,
} from '@finley-technologies/finley-types';
import { extractBorrowingBaseMechanicsConfig } from '@finley-technologies/pointer-methods';

export const sumTotalCommitmentAmount = (
  capitalProviders: CreditAgreement.DomainEntity.Items.CapitalProviderEntity[],
) => {
  finleyAssert(
    capitalProviders.length > 0,
    'Capital providers length must be greater than 0',
  );
  return _.sumBy(
    capitalProviders,
    (capitalProvider) => capitalProvider.commitmentAmount.value,
  );
};

export const parseInterestRateDisplayForCapitalProvider = (
  capitalProvider: CreditAgreement.DomainEntity.Items.CapitalProviderEntity,
) => {
  let interestRateDisplay: string = '';
  switch (capitalProvider._config_.interestRateBase) {
    case CreditAgreement.DomainEntity.Items.InterestRateBase.Fixed:
      if (capitalProvider.interestRateFixedBase) {
        interestRateDisplay = commonUtils.numberToFormattedString(
          capitalProvider.interestRateFixedBase.value,
          'percentage',
          false,
        );
      }
      break;
    case CreditAgreement.DomainEntity.Items.InterestRateBase.Benchmark:
      if (capitalProvider.interestRateBenchmark) {
        interestRateDisplay += capitalProvider.interestRateBenchmark.value;
      }
      if (capitalProvider.applicableMarginBeginning) {
        interestRateDisplay += ' + ';
        interestRateDisplay += commonUtils.numberToFormattedString(
          capitalProvider.applicableMarginBeginning.value,
          'percentage',
          false,
        );
      }
      break;
    default:
      interestRateDisplay = '';
  }

  return interestRateDisplay;
};

export const getClassToCapitalProviderMap = ({
  capitalProviders,
  classMechanics,
}: {
  capitalProviders: CreditAgreement.DomainEntity.Items.CapitalProviderEntity[];
  classMechanics: CreditAgreement.DomainEntity.ClassMechanicsWithEntities;
}) => {
  return _.reduce(
    classMechanics.classes,
    (acc, classMechanic) => {
      const classLabel = classMechanic.classLabel.value;
      const capitalProviderOrganizationId =
        classMechanic.capitalProviders[0].entity.id;
      const capitalProviderFacilityMechanics = _.find(
        capitalProviders,
        ({ entity: { id } }) => id === capitalProviderOrganizationId,
      );

      return { ...acc, [classLabel]: capitalProviderFacilityMechanics };
    },
    {} as Partial<
      {
        [key in CreditFacility.ClassLabel]: CreditAgreement.DomainEntity.Items.CapitalProviderEntity;
      }
    >,
  );
};

// extract relevant borrowing base mechanics info from given CA artifact
export const parseBorrowingBaseMechanics = (
  creditAgreementArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
) => {
  const sourceBorrowingBaseMechanics =
    creditAgreementArtifact.borrowingBaseMechanics.calcAgentMechanics;
  const extractedBorrowingBaseMechanics = extractBorrowingBaseMechanicsConfig(
    sourceBorrowingBaseMechanics,
  );
  // TODO: support multi loan product (https://linear.app/finleycms/issue/ENG-172/support-multi-loan-product-for-calc-agent-qa)
  return {
    borrowingBaseMechanics: extractedBorrowingBaseMechanics,
    loanProductMechanics: extractedBorrowingBaseMechanics.loanProducts[0],
    loanTapeType:
      sourceBorrowingBaseMechanics.loanProductMechanics[0].entity.loanTapeType,
  };
};

const FUNDING_REQUEST_PROPERTIES_WITH_NO_EFFECT: {
  [key in keyof CreditAgreement.DomainEntity.FundingRequestMechanics]-?: Set<
    keyof Exclude<
      CreditAgreement.DomainEntity.FundingRequestMechanics[key],
      undefined
    >
  >;
} = {
  properties: new Set([]),
  drawRequest: new Set(['requiredDocumentation']),
  collectionsPaydown: new Set([
    'incrementAmount',
    'maximumFrequency',
    'requiredDocumentation',
  ]),
  collectionsRecycle: new Set([
    'incrementAmount',
    'minimumAmount',
    'requiredDocumentation',
  ]),
  curePaydown: new Set([
    'incrementAmount',
    'maximumFrequency',
    'minimumAmount',
    'requiredDocumentation',
  ]),
};

export const checkHasNoEffect = ({
  basePropertyKey,
  propertyKey,
}: {
  basePropertyKey: string;
  propertyKey: string;
}): boolean => {
  const [section, baseComponent] = basePropertyKey.split('.');

  if (
    section === 'fundingRequestMechanics' &&
    commonTypeguards.isFundingRequestGeneralPropertiesOrComponentType(
      baseComponent,
    )
  ) {
    const properties: Set<string> =
      FUNDING_REQUEST_PROPERTIES_WITH_NO_EFFECT[baseComponent];

    return properties.has(propertyKey);
  }

  return false;
};
